import { Link, useRouterState } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import {
  Home, BookOpen, Library, Sparkles, TrendingUp, Users, GraduationCap,
  Settings, LogOut, Menu, X, ListChecks, School, Link2, ClipboardList,
  CalendarCheck, FileText, ClipboardCheck, FolderOpen, MessageSquare, BarChart3, Brain, Video, Wand2, ScrollText, Wallet,
  Cpu, Megaphone, Bus, Utensils, HeartPulse, CalendarDays, PenTool, ScrollText as ReportIcon, Heart, Bot, Trophy, AlertCircle, RefreshCw, CloudDownload,
} from "lucide-react";
import { BrandWord, BrandMark } from "./brand";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSession, signOut, type Role } from "@/lib/session";
import { ConnectionBadge } from "@/components/offline/connection-badge";
import { moduleForRoute, useMySchoolId, useSchoolModules } from "@/lib/school-modules";

interface NavItem { to: string; label: string; short?: string; icon: typeof Home }

const NAV: Record<Role, NavItem[]> = {
  learner: [
    { to: "/learner",             label: "Home",        icon: Home },
    { to: "/os",                  label: "Makai OS",    short: "OS", icon: Cpu },
    { to: "/learner/kids",        label: "Kids Zone",   short: "Kids",    icon: Heart },
    { to: "/learner/tutor",       label: "MAKAI Tutor", short: "Tutor",   icon: Sparkles },
    { to: "/learner/coach",       label: "Revision Coach", short: "Coach", icon: Brain },
    { to: "/learner/subjects",    label: "Subjects",    icon: BookOpen },
    { to: "/learner/lessons",     label: "Lessons",     icon: ListChecks },
    { to: "/learner/live",        label: "Live",        icon: Video },
    { to: "/learner/assignments", label: "Assignments", icon: FileText },
    { to: "/learner/quizzes",     label: "Quizzes",     icon: ClipboardList },
    { to: "/learner/library",     label: "Library",     icon: Library },
    { to: "/learner/tree",        label: "Tree",        icon: Sparkles },
    { to: "/learner/twin",        label: "My Twin",     short: "Twin", icon: Brain },
    { to: "/learner/how-i-learn", label: "How I learn", short: "How", icon: Sparkles },
    { to: "/learner/mistakes",    label: "Mistakes",    icon: AlertCircle },
    { to: "/learner/retention",   label: "Quick check", short: "Check", icon: RefreshCw },
    { to: "/learner/grades",      label: "Grades",      icon: BarChart3 },
    { to: "/learner/reports",     label: "AI Reports",  short: "Reports", icon: ReportIcon },
    { to: "/learner/achievements",label: "Achievements",icon: Trophy },
    { to: "/learner/medals",      label: "Medals",      icon: Trophy },
    { to: "/learner/portfolio",   label: "Portfolio",   icon: FolderOpen },
    { to: "/learner/downloads",   label: "Downloads",   short: "Offline", icon: CloudDownload },
    { to: "/learner/comms",       label: "Messages",    icon: MessageSquare },
    { to: "/learner/fees",        label: "Fees",        icon: Wallet },
  ],
  parent: [
    { to: "/parent",              label: "Overview",      icon: Home },
    { to: "/os",                  label: "Makai OS",    short: "OS", icon: Cpu },
    { to: "/parent/companion",    label: "Companion",     short: "Companion", icon: Heart },
    { to: "/parent/tutor",        label: "MAKAI Tutor",   short: "Tutor", icon: Sparkles },
    { to: "/parent/homework",     label: "Homework",      icon: ClipboardList },
    { to: "/parent/timetable",    label: "Timetable",     icon: CalendarCheck },
    { to: "/parent/messages",     label: "Messages",      icon: MessageSquare },
    { to: "/parent/comms",        label: "Messages",      icon: MessageSquare },
    { to: "/parent/announcements",label: "Announcements", icon: Megaphone },
    { to: "/parent/events",       label: "Events",        icon: CalendarDays },
    { to: "/parent/transport",    label: "Transport",     icon: Bus },
    { to: "/parent/cafeteria",    label: "Cafeteria",     icon: Utensils },
    { to: "/parent/medical",      label: "Medical",       icon: HeartPulse },
    { to: "/parent/fees",         label: "Fees",          icon: Wallet },
    { to: "/parent/link",         label: "Link child",    icon: Link2 },
  ],

  teacher: [
    { to: "/teacher",              label: "Dashboard",   icon: Home },
    { to: "/os",                  label: "Makai OS",    short: "OS", icon: Cpu },
    { to: "/teacher/intelligence", label: "Intelligence",short: "Intel", icon: Brain },
    { to: "/teacher/assistant",    label: "AI Assistant",short: "AI",    icon: Bot },

    { to: "/teacher/tutor",        label: "MAKAI Tutor", short: "Tutor", icon: Sparkles },
    { to: "/teacher/studio",       label: "Curriculum Studio", short: "Studio", icon: Wand2 },
    { to: "/teacher/generator",    label: "AI Lesson",   short: "Lesson",icon: Wand2 },
    { to: "/teacher/question-generator", label: "AI Questions", short: "Questions", icon: Sparkles },
    { to: "/teacher/exams",        label: "AI Exam",     icon: ScrollText },
    { to: "/teacher/classes",      label: "Classes",     icon: Users },
    { to: "/teacher/timetable",    label: "Timetable",   icon: CalendarDays },
    { to: "/teacher/attendance",   label: "Attendance",  icon: CalendarCheck },
    { to: "/teacher/live",         label: "Live",        icon: Video },
    { to: "/teacher/planner",      label: "Planner",     icon: FileText },
    { to: "/teacher/assignments",  label: "Assignments", icon: ClipboardList },
    { to: "/teacher/quizzes",      label: "Quizzes",     icon: Sparkles },
    { to: "/teacher/question-bank",label: "Bank",        icon: Library },
    { to: "/teacher/questions",    label: "Curriculum Q",icon: Library },
    { to: "/teacher/gradebook",    label: "Gradebook",   icon: BarChart3 },
    { to: "/teacher/reports/cards",label: "Report Cards",icon: ReportIcon },
    { to: "/teacher/behaviour",    label: "Behaviour",   icon: Heart },
    { to: "/teacher/whiteboard",   label: "Whiteboard",  icon: PenTool },
    { to: "/teacher/resources",    label: "Resources",   icon: FolderOpen },
    { to: "/teacher/comms",        label: "Messages",    icon: MessageSquare },
  ],
  admin: [
    { to: "/admin", label: "School",  icon: School },
    { to: "/os",    label: "Makai OS", short: "OS", icon: Cpu },
  ],
};

const ROLE_LABEL: Record<Role, string> = {
  learner: "Learner",
  parent: "Parent",
  teacher: "Teacher",
  admin: "School Admin",
};

export function AppShell({
  role,
  children,
  title,
  subtitle,
  action,
}: {
  role: Role;
  children: ReactNode;
  title?: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  const schoolId = useMySchoolId();
  const { isEnabled } = useSchoolModules(schoolId);
  // Hide navigation for modules this school has switched off.
  const items = NAV[role].filter((item) => {
    const key = moduleForRoute(item.to);
    return key ? isEnabled(key) : true;
  });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { session } = useSession();
  const [open, setOpen] = useState(false);

  const initials = (session?.name ?? "Guest")
    .split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur">
        <div className="mx-auto grid max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-3 lg:px-8">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="icon"
              className="lg:hidden"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((o) => !o)}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Link to="/" className="hidden sm:block">
              <BrandWord />
            </Link>
            <Link to="/" className="sm:hidden" aria-label="MAKAI home">
              <BrandMark />
            </Link>
          </div>

          <div className="hidden min-w-0 items-center gap-2 lg:flex">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-3 py-1 text-xs font-semibold text-primary">
              <GraduationCap className="h-3.5 w-3.5" /> {ROLE_LABEL[role]}
            </span>
            {session?.grade && (
              <span className="rounded-full bg-gold/15 px-3 py-1 text-xs font-semibold text-gold-foreground">
                {session.grade}
              </span>
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            <ConnectionBadge />
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold leading-tight">{session?.name ?? "Guest"}</div>
              <div className="text-xs text-muted-foreground">{session?.school ?? "MAKAI"}</div>
            </div>
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-gold text-sm font-bold text-gold-foreground">
              {initials}
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-6 lg:px-8 lg:py-8">
        {/* Sidebar (desktop) */}
        <aside className="hidden w-60 shrink-0 lg:block">
          <nav className="sticky top-24 space-y-1">
            {items.map((item) => {
              const active = item.to === "/" + role
                ? pathname === item.to
                : pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                    active
                      ? "bg-primary-soft text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <item.icon className="h-4 w-4" /> {item.label}
                </Link>
              );
            })}
            <div className="my-3 border-t border-border/70" />
            <button
              type="button"
              onClick={() => { signOut().then(() => { window.location.href = "/"; }); }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </nav>
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-foreground/30" onClick={() => setOpen(false)} />
            <div className="absolute inset-y-0 left-0 w-72 max-w-[85%] overflow-y-auto bg-card p-4 shadow-card">
              <div className="mb-4 flex items-center justify-between">
                <BrandWord />
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Close menu">
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <nav className="space-y-1">
                {items.map((item) => {
                  const active = pathname === item.to || (item.to !== "/" + role && pathname.startsWith(item.to));
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className={cn(
                        "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium",
                        active ? "bg-primary-soft text-primary" : "text-foreground hover:bg-muted",
                      )}
                    >
                      <item.icon className="h-4 w-4" /> {item.label}
                    </Link>
                  );
                })}
                <button
                  type="button"
                  onClick={() => { signOut().then(() => { window.location.href = "/"; }); }}
                  className="mt-3 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </nav>
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1">
          {(title || action) && (
            <div className="mb-6 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-4">
              <div className="min-w-0">
                {title && <h1 className="truncate font-display text-2xl font-bold sm:text-3xl">{title}</h1>}
                {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
              </div>
              {action}
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Bottom nav (mobile) */}
      <nav className="sticky bottom-0 z-30 grid grid-cols-5 border-t border-border/70 bg-background/95 backdrop-blur lg:hidden">
        {items.slice(0, 5).map((item) => {
          const active = pathname === item.to || (item.to !== "/" + role && pathname.startsWith(item.to));
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex min-w-0 flex-col items-center gap-1 px-1 py-2 text-[10px] font-medium leading-tight",
                active ? "text-primary" : "text-muted-foreground",
              )}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span className="w-full truncate text-center">{item.short ?? item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export function Settings_Unused() { return <Settings />; }
